import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";

import {
  HyphenIcon,
  Loader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import {
  SortDirection,
  getSortMethod,
} from "@transfr-inc/dashboard-components/data-table";
import {
  SortHeader,
  useSortHeader,
} from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { TagsDropdown } from "@components/custom/dropdowns";
import { DeviceTagsModalSlides } from "@components/modals";
import { SupportCenterButton } from "@components/support-center-button";
import { useContainer } from "@contexts/container-context";
import { useNotificationContext } from "@contexts/notification-context";
import { ZeroStateMessage } from "@views/shared";

import {
  useCurrentUser,
  useDeviceLastOnlineEnabled,
  useOrganizationDeviceCount,
  useOrganizationDevices,
  useOrganizationTags,
  useSetDeviceTags,
} from "@hooks/queries";
import { TagType } from "@models";
import { ClickLinkEvent } from "@models/tracking/xapi-events";
import { useViewHeadsetStatusTracking } from "@views/devices/tracking";

import {
  DeviceAccordion,
  DevicesSearchbox,
  ManageTagsButton,
} from "./components";

import "./index.scss";

function DevicesSortHeader({ showLastOnline, onSortHandler }) {
  const sortHeader = useSortHeader({
    data: [
      { title: "Device Name", propName: "name" },
      { title: "Battery", propName: "batteryLevel" },
      { title: "Activity", propName: "online" },
      ...(showLastOnline
        ? [{ title: "Last Online", propName: "lastOnline" }]
        : []),
      { title: "Location", propName: "location" },
      { title: "Firmware Version", propName: "firmwareVersion" },
      { title: "Status", propName: "status" },
      { title: "Tags", propName: "tags", disabled: true },
      { title: "Casting", propName: "castingLink", disabled: true },
    ],
    onSortHandler,
  });

  return <SortHeader headers={sortHeader.headers} onSort={sortHeader.onSort} />;
}

export function DevicesList({ responsiveStyles = {} }) {
  const { organizationCode } = useCurrentUser() || {};
  const queryClient = useQueryClient();

  const { data: lastOnlineFlag } = useDeviceLastOnlineEnabled();
  const showLastOnline = !!lastOnlineFlag?.enabled;

  const { organizationService } = useContainer();
  const { addNotification } = useNotificationContext();
  const tracking = useViewHeadsetStatusTracking();

  const [devices, setDevices] = useState();
  const [configurations, setConfigurations] = useState([]);
  const [deviceConfigErrorIds, setDeviceConfigErrorIds] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState();
  const [filters, setFilters] = useState({});
  // Which slide the (shared) tags modal opens to; undefined means closed.
  const [tagsModalSlide, setTagsModalSlide] = useState();

  const { data: deviceCount } = useOrganizationDeviceCount(organizationCode);

  const {
    data: organizationDevicesResponse,
    isFetching: fetchingDevices,
    isError: organizationDevicesError,
  } = useOrganizationDevices(organizationCode);

  const { data: organizationDeviceTags } = useOrganizationTags(
    organizationCode,
    TagType.DEVICE
  );

  const { mutateAsync: setDeviceTags, isPending: updatingDeviceTags } =
    useSetDeviceTags(organizationCode);

  const fetchBatchOfConfigurations = (batchOfIds) => {
    organizationService
      .getDeviceConfigurations(organizationCode, batchOfIds)
      .then((newConfigsArray) => {
        setConfigurations((prevConfigs) => {
          const updatedConfigs = [...prevConfigs];

          const existingConfigMap = new Map();
          for (const [index, config] of prevConfigs.entries()) {
            existingConfigMap.set(config.configurationId, index);
          }

          for (const newConfig of newConfigsArray) {
            if (existingConfigMap.has(newConfig.configurationId)) {
              const indexToUpdate = existingConfigMap.get(
                newConfig.configurationId
              );
              updatedConfigs[indexToUpdate] = newConfig;
            } else {
              updatedConfigs.push(newConfig);
            }
          }

          return updatedConfigs;
        });
      })
      .catch(() => {
        setDeviceConfigErrorIds((prevErrorIds) => [
          ...prevErrorIds,
          ...batchOfIds,
        ]);
      });
  };

  const updateDeviceTags = (device) => async (tags) => {
    const tagIds = tags?.map((t) => t.id) ?? [];
    const { id: deviceId } = device;
    try {
      await setDeviceTags({ deviceId, tagIds });
      const devicesUpdated = devices.map((d) =>
        d.id === deviceId ? { ...d, tags } : d
      );
      setDevices(devicesUpdated);
      // Keep the cached devices in sync
      queryClient.setQueryData(
        ["organizationDevices", organizationCode],
        () => devicesUpdated
      );
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const applyFilter = (key, value) =>
    setFilters((v) => ({ ...v, [key]: value }));

  useEffect(() => {
    const { searchResults, tag } = filters || {};
    let filtered = devices;

    if (searchResults) {
      filtered = filtered?.filter((d) =>
        searchResults?.some((r) => r.id === d.id)
      );
    }

    if (tag) {
      filtered = filtered?.filter((d) => d.tags?.some((t) => t.id === tag.id));
    }

    setFilteredDevices(filtered);
  }, [filters, devices]);

  useEffect(() => {
    if (!devices || devices.length === 0) {
      return;
    }

    const uniqueConfigIds = [
      ...new Set(devices.map((device) => device.configurationId)),
    ];

    const BATCH_SIZE = 10;
    for (let i = 0; i < uniqueConfigIds.length; i += BATCH_SIZE) {
      const batchOfIds = uniqueConfigIds.slice(i, i + BATCH_SIZE);

      fetchBatchOfConfigurations(batchOfIds);
    }
  }, [devices]);

  useEffect(() => {
    if (organizationDevicesError) {
      addNotification({
        type: NotificationType.error,
        message:
          "An error has occurred. Please try reloading the page. If this issue persists please contact customer support",
      });
    }
  }, [organizationDevicesError]);

  useEffect(
    () => setDevices(organizationDevicesResponse ?? []),
    [organizationDevicesResponse]
  );

  const pageHeader = (
    <PageHeader title={"Devices"} badge={deviceCount || <HyphenIcon />}>
      <ManageTagsButton
        organizationCode={organizationCode}
        open={tagsModalSlide != null}
        initialSlide={tagsModalSlide ?? DeviceTagsModalSlides.LIST}
        onOpen={() => setTagsModalSlide(DeviceTagsModalSlides.LIST)}
        onClose={() => setTagsModalSlide()}
      />
    </PageHeader>
  );

  const noResults =
    !fetchingDevices && devices?.length > 0 && filteredDevices?.length === 0;

  const handleSort = (header, sortDirection) => {
    const sortDirectionChar =
      sortDirection === SortDirection.DESCENDING ? "-" : "+";
    const sortParam = [`${sortDirectionChar}${header.propName}`];
    const sortFunc = getSortMethod(sortParam);
    setDevices((v) => v?.toSorted(sortFunc));
  };

  return (
    <PageLayout
      className={clsx("devices-list-page", showLastOnline && "has-last-online")}
      header={pageHeader}
      organizationFilterEnabled={false}
      responsiveStyles={responsiveStyles}
    >
      {(fetchingDevices || updatingDeviceTags) && <Loader fixed overlay />}

      <div className="devices-list-toolbar">
        <div className="device-filters-container">
          <DevicesSearchbox
            data={devices}
            setFilteredDevices={(results) =>
              applyFilter("searchResults", results)
            }
            tracking={tracking}
            organizationCode={organizationCode}
          />
          <TagsDropdown
            selected={filters.tag}
            onOptionSelected={(tag) => applyFilter("tag", tag)}
            values={organizationDeviceTags}
            disabled={!organizationDeviceTags?.length}
          />
        </div>
        <div className="support-link-container">
          <span>Need help troubleshooting?</span>
          <SupportCenterButton
            appendUrl="/article/how-to-view-and-monitor-device-status"
            onClick={() =>
              tracking.trackEvent(
                new ClickLinkEvent(
                  "view_headset_device_support_center_link",
                  "view_headset_device_support_center_link"
                )
              )
            }
          ></SupportCenterButton>
        </div>
      </div>
      <div className="devices-accordions-container">
        {noResults ? (
          <ZeroStateMessage message="No devices match the selected filters." />
        ) : (
          <>
            <DevicesSortHeader
              key={showLastOnline}
              showLastOnline={showLastOnline}
              onSortHandler={handleSort}
            />
            {filteredDevices?.map((device) => (
              <DeviceAccordion
                organizationDeviceTags={organizationDeviceTags}
                updateDeviceTags={updateDeviceTags(device)}
                onCreateTag={() =>
                  setTagsModalSlide(DeviceTagsModalSlides.FORM)
                }
                device={device}
                configurations={configurations}
                key={device.id}
                responsiveStyles={responsiveStyles}
                tracking={tracking}
                deviceConfigErrorIds={deviceConfigErrorIds}
                showLastOnline={showLastOnline}
              />
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
}
